import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import Odometer from 'react-odometerjs';
import { userDetail } from 'Stores/userDetail';
import advSearchState from 'Stores/advSearch';
import * as CONSTANTS from 'Utils/constants';
import { freeTrialPopupState } from 'Stores/freeTrialPopupState';
import { Utils } from 'Utils/utils';
import { toasterState } from 'Components/common/toaster';
import { TrialEndInfo } from '../trialUserOnboarding/trialEndInfo';
import Button from '../common/Button';
import { NotificationBar } from './notificationBar';
import { AccountProfileWrapper } from './accountProfileWrapper';
import { NavigationWrapper } from './navigationWrapper';

const { PAGE_PATH_MAP, MX_PAYG_PRICING_EVENT } = CONSTANTS;

const TopNav = observer(props => {
  const { userInfo, featureFlags, showNotificationType = '' } = userDetail;
  const { views, downloads, lbCredits, enrichmentCreditsRemaining } = userInfo;
  const { LEAD_BUILDER = false } = featureFlags;
  const [trialApiLoading, setTrialApiLoading] = useState(false);

  const [credits, setCredits] = useState(lbCredits);

  const { location } = props;
  const { pathname } = location;

  const isPricingPage = () => {
    return pathname === '/pricing';
  };

  const isBuyMoreCreditsPage = () => {
    return pathname === PAGE_PATH_MAP.BUY_MORE_CREDITS;
  };

  const handleUpgradeNowClick = () => {
    Utils.upgradeNowClick('Header');
  };

  const handleStartFreeTrialClick = () => {
    setTrialApiLoading(true);
    if (!trialApiLoading) {
      const failedCallback = () => {
        setTrialApiLoading(false);
        toasterState.setToastMsg(
          'Unable to process your request now, please try again later ',
          'failure',
        );
      };
      freeTrialPopupState.processTrialRequest(failedCallback);
    }
  };

  const buyMoreCredits = () => {
    Utils.mixpanelTrack(
      MX_PAYG_PRICING_EVENT.LB_PAYG_BUY_MORE_HEADER_CLICKED,
      {},
    );
    Utils.redirectToBuyMoreCreditsPage();
  };

  React.useEffect(() => {
    if (
      !userDetail.isFreeTrial ||
      (userDetail.isFreeTrial &&
        advSearchState.asyncPurchaseOrExportMsg === '' &&
        advSearchState.syncPurchaseOrExportData === null)
    )
      setCredits(lbCredits);
  }, [
    userInfo.lbCredits,
    setCredits,
    advSearchState.asyncPurchaseOrExportMsg,
    advSearchState.syncPurchaseOrExportData,
  ]);

  const isMonthCreditLimitAvailable =
    userInfo.creditLimitEnabled && userInfo.remainingIndividualLbCredits !== -1;

  const showEnrichmentCreditsInHeader =
    pathname.includes('/enrichment') && Utils.isCrmEnrichmentEnabled();

  return (
    <>
      <NotificationBar notificationType={showNotificationType} {...props} />
      <header className="header">
        <NavigationWrapper {...props} />
        <div className="nav-wrapper-right">
          <ul className="credit-and-upgrade-section">
            {/* Free user trial not taken */}
            {!LEAD_BUILDER && !Utils.isPaidUser() && !Utils.isTrialUsed() && (
              <li>
                <Button
                  btnText="Start Free Trial"
                  icon={false}
                  iconName=""
                  type="button"
                  className={`upgrade-header-btn free-trial-btn ${
                    trialApiLoading ? ' loading' : ''
                  }`}
                  loader={trialApiLoading}
                  click={e => handleStartFreeTrialClick(e)}
                />
              </li>
            )}

            {/* Active Trial Users */}
            {userDetail.isFreeTrial && (
              <>
                <li>
                  <TrialEndInfo customClass="header-trial-info" />
                </li>

                {!isPricingPage() && !Utils.isPaymentPendingReview() && (
                  <li>
                    <Button
                      btnText="Upgrade Now"
                      icon={false}
                      iconName=""
                      type="button"
                      className="upgrade-header-btn"
                      click={e => handleUpgradeNowClick(e)}
                    />
                  </li>
                )}
              </>
            )}

            {/* Free user trial already taken */}
            {((!LEAD_BUILDER && !Utils.isPaidUser() && Utils.isTrialUsed()) ||
              Utils.isFreemiumPlanActive()) &&
              !isPricingPage() && (
                <li>
                  <Button
                    btnText="Upgrade Now"
                    icon={false}
                    iconName=""
                    type="button"
                    className="upgrade-header-btn"
                    click={e => handleUpgradeNowClick(e)}
                  />
                </li>
              )}

            {/* Selfserve paid user with new PAYG plan */}
            {LEAD_BUILDER &&
              Utils.isPaygPlan() &&
              !Utils.isFreemiumPlanActive() &&
              !isBuyMoreCreditsPage() && (
                <li>
                  <Button
                    btnText="Buy More Credits"
                    icon={false}
                    iconName=""
                    type="button"
                    className="upgrade-header-btn"
                    click={e => buyMoreCredits(e)}
                  />
                </li>
              )}

            {/* Paid user */}
            {LEAD_BUILDER &&
              !Utils.isCreditBasedSubscription() &&
              !showEnrichmentCreditsInHeader && (
                <>
                  <li className="separate-line">
                    <div>
                      <h4>{Utils.viewsDownloadsNumberFormat(views)}</h4>
                      <span className="count-name">Views</span>
                    </div>
                  </li>
                  <li>
                    <div>
                      <h4>{Utils.viewsDownloadsNumberFormat(downloads)}</h4>
                      <span className="count-name">Downloads</span>
                    </div>
                  </li>
                </>
              )}
            {LEAD_BUILDER &&
              Utils.isCreditBasedSubscription() &&
              !showEnrichmentCreditsInHeader &&
              isMonthCreditLimitAvailable && (
                <li className="separate-line">
                  <div>
                    <h4 className="credits">
                      {Utils.viewsDownloadsNumberFormat(
                        userInfo.remainingIndividualLbCredits,
                      )}
                    </h4>
                    <span className="count-name">Available Monthly Credit</span>
                  </div>
                </li>
              )}
            {LEAD_BUILDER && showEnrichmentCreditsInHeader && (
              <>
                <li className="separate-line">
                  <div>
                    <h4>
                      {Utils.viewsDownloadsNumberFormat(
                        enrichmentCreditsRemaining,
                      )}
                    </h4>
                    <span className="count-name">Enrichment Credits</span>
                  </div>
                </li>
              </>
            )}
          </ul>
          <AccountProfileWrapper />
        </div>
      </header>
    </>
  );
});

export default withRouter(TopNav);
